import React, { useEffect, useRef, useState } from "react";
import { api, resolveMediaUrl } from "@/lib/api";
import { useSharedHeroPhotos } from "@/hooks/useSharedHeroPhotos";
import { useResilientVideoPlayback } from "@/hooks/useResilientVideoPlayback";

/**
 * HomeCinematicBackground
 * -----------------------
 * A SINGLE sticky background layer spanning Hero + Manifesto + Directory.
 *
 * Rendering contract — DEFENSIVE LAYERING:
 *   Layer 0 : Solid espresso base (`#0F0C0B`) — guaranteed non-empty.
 *   Layer 1 : Crossfading photo showreel — always rendered when photos
 *             exist. This is the *permanent* visual fallback, so if the
 *             video is loading, paused, blocked by autoplay policy, or
 *             fails permanently, the visitor still sees cinematic motion.
 *   Layer 2 : Uploaded MP4 (when configured) — mounted over the photos
 *             but fades in only after the `playing` event fires. If it
 *             never fires (e.g. codec unsupported after retries), the
 *             element stays at opacity 0 and the photos remain visible.
 *   Layer 3 : Film-grade darkening wash — over motion, under copy.
 *
 * Only DIRECT video files (.mp4/.webm/.mov/.m4v) are eligible for the
 * background. YouTube/Vimeo iframes always leak player chrome, so we
 * intentionally reject them here (they are still allowed in admin as
 * modal-style archival content, but never mounted as a background).
 */

const CROSSFADE_MS = 1200;
const KEN_BURNS_MS = 7000;
const MOBILE_QUERY = "(max-width: 767px)";

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.matchMedia(MOBILE_QUERY).matches
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia(MOBILE_QUERY);
    const onChange = (e) => setIsMobile(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);
  return isMobile;
}

function isDirectVideo(url, kind) {
  if (!url) return false;
  if (kind === "file") return true;
  return /\.(mp4|webm|mov|m4v)(\?.*)?$/i.test(url);
}

export function HomeCinematicBackground() {
  const { photos, current, loaded } = useSharedHeroPhotos();
  const [settings, setSettings] = useState(null);
  const [isActive, setIsActive] = useState(true);
  const isMobile = useIsMobile();

  useEffect(() => {
    let cancelled = false;
    api.get("/settings")
      .then(({ data }) => { if (!cancelled) setSettings(data); })
      .catch(() => { /* fall back to photo showreel */ });
    return () => { cancelled = true; };
  }, []);

  // Pause the <video> when the visitor reaches the end of the home
  // chapter (i.e. the footer is coming into view).
  useEffect(() => {
    const sentinel = document.querySelector('[data-testid="home-chapter-end-sentinel"]');
    if (!sentinel) return;
    const obs = new IntersectionObserver(
      ([entry]) => setIsActive(!entry.isIntersecting),
      { threshold: 0 }
    );
    obs.observe(sentinel);
    return () => obs.disconnect();
  }, []);

  const url = settings?.manifesto_showreel_url || "";
  const kind = settings?.manifesto_showreel_kind || "";
  const configuredForVideo = isDirectVideo(url, kind);

  // Drive resilient autoplay + recovery. `enabled` is only true when a
  // playable video is configured AND the home chapter is in view.
  const { videoRef, isPlaying: videoIsPlaying, hasFailed: videoFailed } =
    useResilientVideoPlayback({
      enabled: configuredForVideo && isActive,
      maxRetries: 5,
    });

  // Show the video overlay only when the browser is actually painting
  // frames. Never expose the frozen first frame or a black loading gap.
  const showVideoLayer = configuredForVideo && !videoFailed;
  const videoVisible = showVideoLayer && videoIsPlaying;

  const currentPhoto = photos[current];
  const posterSrc =
    resolveMediaUrl(settings?.manifesto_showreel_poster || "") ||
    (currentPhoto ? resolveMediaUrl(currentPhoto.image_url) : "");

  return (
    <div
      data-testid="home-cinematic-background"
      className="sticky top-0 h-screen w-full overflow-hidden bg-[#0F0C0B]"
      aria-hidden="true"
    >
      {/* Layer 1 — permanent photo showreel fallback. Always rendered
          when photos exist, so if the video is not playing (loading,
          blocked, or failed) we still show cinematic motion instead of
          a black rectangle. */}
      {loaded && photos.length > 0 ? (
        photos.map((p, i) => (
          <ShowreelFrame
            key={p.id}
            photo={p}
            active={i === current}
            animate={isActive}
            isMobile={isMobile}
          />
        ))
      ) : (
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at 60% 55%, #2A1F1C 0%, #1A1514 42%, #0F0C0B 100%)",
          }}
        />
      )}

      {/* Layer 2 — uploaded MP4. Rendered over the photos but faded in
          only when the `playing` event fires. On permanent failure the
          element is unmounted entirely and the photo layer takes over
          seamlessly. */}
      {showVideoLayer && (
        <video
          ref={videoRef}
          src={resolveMediaUrl(url)}
          poster={posterSrc || undefined}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          disableRemotePlayback
          disablePictureInPicture
          x-webkit-airplay="deny"
          controlsList="nodownload nofullscreen noremoteplayback"
          className="absolute inset-0 w-full h-full object-cover select-none pointer-events-none"
          style={{
            opacity: videoVisible ? 1 : 0,
            transition: "opacity 900ms cubic-bezier(0.22, 1, 0.36, 1)",
            willChange: "opacity",
          }}
          data-testid="home-background-video"
          onContextMenu={(e) => e.preventDefault()}
        />
      )}

      {/* Layer 3 — film-grade darkening for video mode. Videos have
          uncontrollable per-frame luminance, so this translucent
          espresso wash gives copy on every section a floor of contrast
          without ever fully hiding the motion beneath. */}
      {videoVisible && (
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{ background: "rgba(15,12,11,0.42)" }}
        />
      )}
    </div>
  );
}

/**
 * A single crossfading photo frame — used as the permanent background
 * layer (and the sole background when no direct-video showreel is
 * configured). Uses per-device (desktop/mobile) crop metadata + a slow
 * Ken Burns drift.
 */
function ShowreelFrame({ photo, active, animate, isMobile }) {
  const src = resolveMediaUrl(photo.image_url);
  const isPortrait = photo.orientation === "portrait";
  const legacyY = typeof photo.focal_y === "number" ? photo.focal_y : (isPortrait ? 0.3 : 0.5);
  const crop = isMobile
    ? (photo.hero_mobile || { x: 0.5, y: legacyY, scale: 1.0 })
    : (photo.hero_desktop || { x: 0.5, y: legacyY, scale: 1.0 });
  const objectPosition = `${Math.round((crop.x ?? 0.5) * 100)}% ${Math.round((crop.y ?? 0.5) * 100)}%`;
  const baseScale = Number.isFinite(crop.scale) && crop.scale > 0 ? crop.scale : 1.0;
  const restScale = baseScale;
  const activeScale = baseScale * 1.04;

  return (
    <div
      className="absolute inset-0"
      style={{
        opacity: active ? 1 : 0,
        transition: `opacity ${CROSSFADE_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`,
        zIndex: active ? 2 : 1,
      }}
    >
      <img
        src={src}
        alt=""
        loading="lazy"
        decoding="async"
        className="w-full h-full object-cover"
        style={{
          objectPosition,
          transform: `scale(${active && animate ? activeScale : restScale})`,
          transition: `transform ${KEN_BURNS_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`,
          transformOrigin: "center center",
        }}
        draggable={false}
      />
    </div>
  );
}
